import { ArrowLeft, ArrowRight, GithubLogo, ShieldWarning } from "@phosphor-icons/react";
import { DownloadPanel } from "../components/DownloadPanel.js";
import { Gallery } from "../components/Gallery.js";
import { StatusBadge } from "../components/StatusBadge.js";
import { tagLabel, text } from "../i18n.js";
import { formatDate } from "../lib/format.js";
import { COMMUNITY_REPOSITORY, localeHref, themeHref, versionHref } from "../lib/routes.js";
import type { CommunityThemeView, Locale } from "../types.js";

export function ThemePage({ locale, theme }: { readonly locale: Locale; readonly theme: CommunityThemeView }) {
  const t = text(locale);
  const content = theme.localized[locale];
  const latest = theme.versions.find(({ version }) => version === theme.latestVersion) ?? theme.versions[0];
  if (!latest) return null;
  const media = [latest.preview, ...latest.screenshots];
  return (
    <>
      <section className="detail-heading-section">
        <div className="container">
          <a className="back-link" href={localeHref(locale)}><ArrowLeft aria-hidden="true" />{t.backToThemes}</a>
          <div className="detail-title-grid">
            <div>
              <div className="detail-status"><StatusBadge locale={locale} status={latest.supportStatus} />{theme.featured ? <span>{locale === "zh-CN" ? "社区精选" : "Community featured"}</span> : null}</div>
              <h1>{content.name}</h1>
              <p>{content.summary}</p>
              <div className="tag-list">{theme.tags.map((tag) => <span key={tag}>{tagLabel(locale, tag)}</span>)}</div>
            </div>
            <dl className="detail-byline">
              <div><dt>{t.author}</dt><dd><a href={`https://github.com/${theme.author.github}`} target="_blank" rel="noreferrer"><GithubLogo aria-hidden="true" />{theme.author.name}</a></dd></div>
              <div><dt>{t.version}</dt><dd>{latest.version}</dd></div>
              <div><dt>{t.released}</dt><dd>{formatDate(latest.releasedAt, locale)}</dd></div>
            </dl>
          </div>
        </div>
      </section>
      <section className="detail-main-section">
        <div className="container detail-main-grid">
          <div className="detail-content">
            <Gallery locale={locale} media={media} themeName={content.name} />
            <section className="prose-section">
              <h2>{locale === "zh-CN" ? "关于这个主题" : "About this theme"}</h2>
              <p>{content.description}</p>
            </section>
            <section className="version-history">
              <div className="section-heading"><h2>{t.versions}</h2><span>{theme.versions.length}</span></div>
              <ol>
                {theme.versions.map((version, index) => (
                  <li key={version.version}>
                    <div><strong>{version.version}</strong><StatusBadge locale={locale} status={version.supportStatus} /></div>
                    <p>{version.changelog[locale]}</p>
                    <span>{formatDate(version.releasedAt, locale)} · OpenChatGPTSkin {version.minimumAppVersion}+</span>
                    <a href={versionHref(locale, theme.id, version.version)}>{index === 0 ? t.latest : t.previous}<ArrowRight aria-hidden="true" /></a>
                  </li>
                ))}
              </ol>
            </section>
            <section className="rights-notice">
              <ShieldWarning aria-hidden="true" weight="thin" />
              <div><h2>{t.rightsTitle}</h2><p>{t.rightsBody}</p>
                <a href={`${COMMUNITY_REPOSITORY}/issues/new?template=report.yml&title=${encodeURIComponent(`[Report] ${theme.id}`)}`}>{t.reportTheme}<ArrowRight aria-hidden="true" /></a>
              </div>
            </section>
          </div>
          <DownloadPanel locale={locale} version={latest} />
        </div>
      </section>
    </>
  );
}
